import type { BrandDto } from '@bk/contracts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, apiObjectUrl } from './api';

const channel = (c: number) => {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};
const luminance = (r: number, g: number, b: number) => 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

const rgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
/** Contrasto del testo bianco su questo colore. */
const onWhite = (hex: string) => 1.05 / (luminance(...rgb(hex)) + 0.05);

/**
 * Il colore del marchio come accento dell'app (ADR 0117): è il colore dei pulsanti con il testo bianco e dei
 * collegamenti sul bianco, quindi deve reggere il testo bianco (contrasto 4,5:1). Un colore principale troppo chiaro (un
 * giallo, un arancio) scurito diventerebbe un marrone: allora si usa il primo altro colore del marchio che si legge già
 * quasi bene; se non c'è, si scurisce il principale mantenendo la tinta.
 */
export function readableAccent(colori: string[]): string {
  const base = onWhite(colori[0]!) >= 3 ? colori[0]! : (colori.slice(1).find((c) => onWhite(c) >= 3) ?? colori[0]!);
  let [r, g, b] = rgb(base);
  for (let i = 0; i < 40 && 1.05 / (luminance(r, g, b) + 0.05) < 4.5; i++) [r, g, b] = [r * 0.93, g * 0.93, b * 0.93];
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}

/** Il marchio di un'azienda (stessa cache ovunque); la lettura parte alla prima richiesta e si riguarda finché finisce. */
export function useBrandData(tenantId: string, enabled = true) {
  return useQuery({
    queryKey: ['tenant', tenantId, 'brand'],
    queryFn: () => api<BrandDto>(`/tenants/${tenantId}/brand`),
    enabled,
    refetchInterval: (q) => (q.state.data && ['da_fare', 'in_corso'].includes(q.state.data.stato) ? 5000 : false),
    staleTime: 10 * 60_000,
  });
}

/**
 * Il logo di un'azienda come indirizzo da mostrare, null se non ce l'ha (ADR 0117). Scaricato una volta e tenuto per
 * tutta la sessione: lo usano il menu, la barra in alto e l'elenco delle aziende.
 */
export function useTenantLogo(tenantId: string, enabled = true): string | null {
  const brand = useBrandData(tenantId, enabled);
  const logo = useQuery({
    queryKey: ['tenant', tenantId, 'brand', 'logo', brand.data?.letto_il ?? null],
    queryFn: () => apiObjectUrl(`/tenants/${tenantId}/brand/logo`),
    enabled: enabled && !!brand.data?.logo,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return brand.data?.logo ? (logo.data ?? null) : null;
}

/**
 * Il marchio dell'azienda aperta: l'accento dell'app prende il suo colore finché si è dentro; il logo da mostrare.
 * Dentro una sede con i suoi colori (ADR 0210 § 3) l'accento è quello della sede: un solo punto decide, così il colore
 * dell'azienda, che arriva dopo, non copre quello della sede.
 */
export function useBrand(tenantId: string, enabled = true, siteColors: string[] = []): { logo: string | null } {
  const brand = useBrandData(tenantId, enabled);
  const own = brand.data?.colore ? [brand.data.colore, ...brand.data.colori.filter((c) => c !== brand.data!.colore)] : [];
  const colori = siteColors.length ? siteColors : own;
  const accent = colori.length ? readableAccent(colori) : null;
  useEffect(() => {
    if (!accent) return;
    const root = document.documentElement.style;
    root.setProperty('--color-accent', accent);
    return () => {
      root.removeProperty('--color-accent');
    };
  }, [accent]);
  return { logo: useTenantLogo(tenantId, enabled) };
}

const LOGO_TONES = new Map<string, 'chiaro' | 'normale'>();

/**
 * Il logo è disegnato per uno sfondo scuro (ADR 0151)? Molti siti hanno il nome in bianco su trasparente: sul bianco
 * della barra si vedrebbe solo un dettaglio colorato. Si guardano i pixel visibili del logo: se sono quasi tutti chiari,
 * il riquadro prende uno sfondo scuro. Il logo è un indirizzo locale (blob), quindi si può leggere.
 */
export function useLogoOnDark(src: string | null): boolean {
  const [tone, setTone] = useState<'chiaro' | 'normale' | null>(() => (src ? (LOGO_TONES.get(src) ?? null) : null));
  useEffect(() => {
    if (!src) return;
    const known = LOGO_TONES.get(src);
    if (known) return setTone(known);
    let live = true;
    const img = new Image();
    img.onload = () => {
      const w = Math.min(img.naturalWidth || 64, 128);
      const h = Math.max(1, Math.round(((img.naturalHeight || 64) * w) / (img.naturalWidth || 64)));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, w, h);
      let px: Uint8ClampedArray;
      try {
        px = ctx.getImageData(0, 0, w, h).data;
      } catch {
        return;
      }
      let seen = 0;
      let light = 0;
      for (let i = 0; i < px.length; i += 4) {
        if (px[i + 3]! < 128) continue;
        seen++;
        if (luminance(px[i]!, px[i + 1]!, px[i + 2]!) > 0.8) light++;
      }
      // Un logo con lo sfondo bianco pieno non ha trasparenza: solo i loghi su trasparente possono sparire nel bianco.
      const transparent = seen < (px.length / 4) * 0.97;
      const t = transparent && seen > 0 && light / seen > 0.7 ? 'chiaro' : 'normale';
      LOGO_TONES.set(src, t);
      if (live) setTone(t);
    };
    img.src = src;
    return () => {
      live = false;
    };
  }, [src]);
  return tone === 'chiaro';
}
